import React, { useContext, useEffect, useRef, useState } from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { isNotNullOrUndefined, openMailClientCustoms } from '@utils/utils.tsx';
import { AdditionalCostsSidebar } from '@components/obsolete/AdditionalCostsSidebar/AdditionalCostsSidebar.tsx';
import ConfirmDialog from '@components/RingilComponents/ConfirmDialog.tsx';
import DuplicateExpeditionDialog from '@components/obsolete/DuplicateExpedition/DuplicateExpeditionDialog.tsx';
import { ApiContext } from '@api/api.tsx';
import SvgDuplicate from '@components/ringil3/icons/menu/reservation/Duplicate.tsx';
import SvgXcircle from '@icons/Xcircle.tsx';
import SvgAlert from '@icons/Alert.tsx';
import SvgMessage from '@icons/Message.tsx';
import SvgArchive from '@icons/Archive.tsx';
import SvgCheckSquare from '@icons/CheckSquare.tsx';
import SvgNumber from '@icons/Number.tsx';
import SvgCoins from '@icons/Coins.tsx';
import SvgCoStar from '@icons/CoStar.tsx';
import SvgPerson from '@icons/person.svg?react';
import {
  ExpeditionDuplicationOptions,
  useCreateDuplicateExpeditionSidebar,
} from '@components/ringil3/Features/Expedition/DuplicateExpeditionSidebar/DuplicateExpeditionSidebar.tsx';
import {
  useCreateCancelExpeditionSidebar,
} from '@components/ringil3/Features/Expedition/Sidebars/CancelExpeditionSidebar.tsx';
import SvgCarPlate from '@components/ringil3/icons/CarPlate.tsx';
import {
  useCreateAssignCarrierSidebar,
} from '@components/ringil3/Features/Expedition/Sidebars/AssignCarrierSidebar.tsx';
import SvgTabAttachments from '@components/ringil3/icons/TabAttachments.tsx';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import { ApiTypeEnum, ExpeditionStatusEnum, FeaturesEnum, PermissionTypesEnum } from '@api/ringil-api.v2.tsx';
import SvgRepeat from '@icons/Repeat.tsx';
import MoreButton from '@components/RingilComponents/MoreButton/MoreButton.tsx';
import { useExpeditionMailto } from '@components/ringil3/Features/Expedition/MoreButton/useExpeditionMailto.ts';
import {
  useExpeditionApproveMailto,
} from '@components/ringil3/Features/Expedition/MoreButton/useExpeditionApproveMailto.ts';
import { RuleInput, SecuredConfig, useSecuredDecoration } from '@components/security/securedDecoration';
import { useSecuredFlag } from '@components/security/useSecuredFlag';
import { EXPEDITION_RULE_DESCRIPTION } from '@components/ringil3/Features/Expedition/expeditionSecurityTexts';

type MenuItemSection = 'state' | 'related' | 'actions';

/** Menu sections in display order, with the titles the three inline blocks used to carry. */
const SECTIONS: { id: MenuItemSection; title: (tr: (key: string, fallback: string) => string) => string }[] = [
  { id: 'state', title: tr => tr('ButtonMoreNavigation.stateSection', 'State') },
  { id: 'related', title: tr => tr('ButtonMoreNavigation.relatedSection', 'Related') },
  { id: 'actions', title: tr => tr('ButtonMoreNavigation.actionsSection', 'Actions') },
];

export interface ExpeditionMoreButtonsExpedition {
  id: string;
  customerId: string;
  applicationId: string;
  currency: string;
  expeditionStatus: ExpeditionStatusEnum;
  isArchived: boolean;
  isFavorite?: boolean;
  isDistributionCreated: boolean;
  linkedToSN: boolean;
  firstLocationName: string;
  assignedDispatcherEmail?: string;
  carrierApiType?: ApiTypeEnum;
  duplicationOptions?: ExpeditionDuplicationOptions;
  showExtraCosts: boolean;
  submitToSapEnabled?: boolean;
  /** Owner company has a webhook definition for expeditions - only the detail page resolves this; list rows leave it undefined. */
  webhookAvailable?: boolean;
}

interface ComponentProps {
  expedition: ExpeditionMoreButtonsExpedition;
  onReload: () => void;
  refreshAttachment?: () => void;
  buttonSize?: 'small' | 'big';
  dataTestId?: string;
  secured?: SecuredConfig;
}

const ExpeditionMoreButtons: React.FC<ComponentProps> = ({
                                                           expedition,
                                                           onReload,
                                                           refreshAttachment = () => {
                                                           },
                                                           buttonSize = 'small',
                                                           dataTestId,
                                                           secured,
                                                         }) => {
  const {
    id: expeditionId,
    customerId,
    applicationId,
    currency,
    expeditionStatus,
    isArchived,
    isFavorite: initialIsFavorite = false,
    isDistributionCreated,
    linkedToSN,
    firstLocationName,
    assignedDispatcherEmail,
    carrierApiType,
    duplicationOptions: expeditionDuplicationOptions,
    showExtraCosts,
    submitToSapEnabled,
    webhookAvailable,
  } = expedition;
  const { tr } = useTranslationLgs();
  const {
    changeArchiveStateForExpedition,
    editInternalInfoExpedition,
    finishExpedition,
    reindexOneExpedition,
    getCustomsForExpedition,
    createOrderConfirmationPdf,
    favoriteExpedition,
    downloadPODFromDachser,
    getDachserStatus,
    sendExternalQuote,
    getExpeditionV4Detail,
    createExpeditionDetailPdf,
    manualWebhookExpeditionSend,
  } =
    useContext(ApiContext);

  const { loggedUser, hasFeature } = useContext(AuthenticatedUserContext);
  const { decorate } = useSecuredDecoration(secured);

  const [visibleConfirmDialog, setVisibleConfirmDialog] = useState(false);
  const [visibleConfirmArchiveWithSNDialog, setVisibleConfirmArchiveWithSNDialog] = useState(false);
  const [visibleDuplicateDialog, setVisibleDuplicateDialog] = useState(false);

  const [visibleAdditionalCosts, setVisibleAdditionalCosts] = useState(false);

  const [visibleFinishDialog, setVisibleFinishDialog] = useState(false);
  const [visibleSapSubmitDialog, setVisibleSapSubmitDialog] = useState(false);
  const [visibleSendWebhookDialog, setVisibleSendWebhookDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isFavorite, setIsFavorite] = useState(initialIsFavorite);

  const { CancelExpeditionSidebar, setCancelationRequest, cancelationRequest } = useCreateCancelExpeditionSidebar();
  const { AssignCarrierSidebar, setVisible: setVisibleAssignCarrierSidebar } = useCreateAssignCarrierSidebar();

  const {
    DuplicateExpeditionSidebar,
    visible: visibleDuplicateSidebar,
    setVisible: setVisibleDuplicateSidebar,
  } = useCreateDuplicateExpeditionSidebar();

  const [open, setOpen] = useState(false);
  const detailsRef = useRef<HTMLDetailsElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent | TouchEvent) => {
      const el = detailsRef.current;
      if (!el) return;
      const target = (e as MouseEvent).target as Node | null;
      if (open && target && !el.contains(target)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [open, detailsRef]);

  const claim_email_body = `Dobr%C3%BD%20den%2C%0D%0A%0D%0Adovolujeme%20si%20v%C3%A1m%20ozn%C3%A1mit%2C%20%C5%BEe%20z%C3%A1silka%20%C4%8D%C3%ADslo%20${applicationId}%20byla%20doru%C4%8Dena%20dne%20(!!!DOPLNIT)%20ve%20stavu%3A%20po%C5%A1kozen%C3%A1%20%2F%20nekompletn%C3%AD.%0D%0A%0D%0AZ%C3%A1silku%20aktu%C3%A1ln%C4%9B%20zpracov%C3%A1v%C3%A1%20n%C3%A1%C5%A1%20sklad.%20Jakmile%20bude%20dokon%C4%8Dena%20kontrola%20produkt%C5%AF%2C%20sd%C4%9Bl%C3%ADme%20v%C3%A1m%20v%C3%BDsledek%20reklamace.%0D%0A%0D%0AS%20pozdravem`;

  const { buildMailto } = useExpeditionMailto();
  const { buildMailto: buildApproveMailto } = useExpeditionApproveMailto();

  const handleMailto = (expeditionId: string, showPrice: boolean) => {
    getExpeditionV4Detail(expeditionId,
      (data) => {
        buildMailto(data, showPrice).then(href => window.location.href = href);
      });
  };

  const handleApproveMailto = (expeditionId: string) => {
    getExpeditionV4Detail(expeditionId,
      (data) => {
        buildApproveMailto(data).then(href => window.location.href = href);
      });
  };

  const openMailClientCustomsForExp = () => {
    openMailClientCustoms(getCustomsForExpedition, expeditionId, firstLocationName, applicationId);
  };

  const isDachserApiCarrier = carrierApiType ? carrierApiType === ApiTypeEnum.DACHSER : false;

  const isImported = expeditionStatus === ExpeditionStatusEnum.IMPORTED;
  const canBeFinished = expeditionStatus !== ExpeditionStatusEnum.FINISHED && expeditionStatus !== ExpeditionStatusEnum.CANCELED;
  const isExpeditionCanceled = expeditionStatus === ExpeditionStatusEnum.CANCELED;

  const notImportedState = { label: 'expeditionStatus ≠ IMPORTED', satisfied: !isImported, description: EXPEDITION_RULE_DESCRIPTION.importedAction };

  const duplicateFlag = useSecuredFlag('more-action-duplicate', {
    kind: 'action',
    description: 'Duplicate expedition (single / multi)',
    permissions: [PermissionTypesEnum.PERMISSION_EXPEDITION_CREATE],
  });
  const cancelFlag = useSecuredFlag('more-action-cancel', {
    kind: 'action',
    description: 'Cancel expedition',
    state: [{ label: 'expeditionStatus ≠ CANCELED', satisfied: !isExpeditionCanceled, description: 'Not offered on a shipment that is already cancelled', byDesign: true }],
  });
  const extraCostsFlag = useSecuredFlag('more-action-extra-costs', {
    kind: 'action',
    description: 'Add extra costs',
    state: [
      { label: 'showExtraCosts', satisfied: showExtraCosts, description: 'Offered only once a carrier is assigned, and not on a new, assigning or cancelled shipment', byDesign: true },
      notImportedState,
    ],
  });
  const assignCarrierFlag = useSecuredFlag('more-action-assign-carrier', {
    kind: 'action',
    description: 'Manually assign carrier',
    state: [
      { label: 'no distribution created', satisfied: !isDistributionCreated, description: 'Offered only before carriers are asked for quotes (no distribution created yet)', byDesign: true },
      { label: 'expeditionStatus ∉ {FINISHED, CANCELED}', satisfied: canBeFinished, description: EXPEDITION_RULE_DESCRIPTION.finishedOrCancelled, byDesign: true },
      notImportedState,
    ],
  });
  const claimFlag = useSecuredFlag('more-action-claim', {
    kind: 'action',
    description: 'Send shipping-claim email',
    features: [FeaturesEnum.EXPEDITION_SHIPPING_CLAIM],
    state: [notImportedState],
  });
  const notifyFlag = useSecuredFlag('more-action-notify', {
    kind: 'action',
    description: 'Send notify mail (with / without price)',
    features: [FeaturesEnum.EXPEDITION_NOTIFY],
    state: [notImportedState],
  });
  const approveMailtoFlag = useSecuredFlag('more-action-approve-mailto', {
    kind: 'action',
    description: 'Send approve-extra-transport mail',
    features: [FeaturesEnum.EXPEDITION_APPROVE_EXTRA_TRANSPORT_MAILTO],
    state: [
      notImportedState,
      { label: 'expeditionStatus === ASSIGNING', satisfied: expeditionStatus === ExpeditionStatusEnum.ASSIGNING, description: 'Offered only while carriers are being asked for quotes (status Assigning)', byDesign: true },
    ],
  });
  const notifyWarehouseFlag = useSecuredFlag('more-action-notify-warehouse', {
    kind: 'action',
    description: 'Notify warehouse mail',
    features: [FeaturesEnum.EXPEDITION_NOTIFY_WAREHOUSE],
    state: [notImportedState],
  });
  const assignMyselfFlag = useSecuredFlag('more-action-assign-myself', {
    kind: 'action',
    description: 'Assign responsible person to current user',
  });
  const archiveFlag = useSecuredFlag('more-action-archive', {
    kind: 'action',
    description: 'Archive expedition',
    state: [{ label: 'not archived', satisfied: !isArchived, description: 'Not offered on a shipment that is already archived', byDesign: true }],
  });
  const archiveWithSNFlag = useSecuredFlag('more-action-archive-with-sn', {
    kind: 'action',
    description: 'Archive expedition with linked SN',
    state: [
      { label: 'not archived', satisfied: !isArchived, description: 'Not offered on a shipment that is already archived', byDesign: true },
      { label: 'linked to SN', satisfied: linkedToSN, description: 'Offered only for a shipment created from a supplier notification', byDesign: true },
    ],
  });
  const unarchiveFlag = useSecuredFlag('more-action-unarchive', {
    kind: 'action',
    description: 'Unarchive expedition',
    state: [{ label: 'is archived', satisfied: isArchived, description: 'Offered only on an archived shipment', byDesign: true }],
  });
  const finishFlag = useSecuredFlag('more-action-finish', {
    kind: 'action',
    description: 'Finish expedition (non-SAP customers)',
    custom: [{ label: 'no SKODA_PUBLISH_EXPEDITION_TO_SAP feature', satisfied: !hasFeature([FeaturesEnum.SKODA_PUBLISH_EXPEDITION_TO_SAP]), description: 'Not offered to companies that publish shipments to SAP; they use Submit to SAP instead' }],
    state: [{ label: 'can be finished', satisfied: canBeFinished, description: EXPEDITION_RULE_DESCRIPTION.finishedOrCancelled, byDesign: true }],
  });
  const submitToSapFlag = useSecuredFlag('more-action-submit-to-sap', {
    kind: 'action',
    description: 'Submit expedition to SAP',
    features: [FeaturesEnum.SKODA_PUBLISH_EXPEDITION_TO_SAP],
    state: [
      { label: 'submitToSap enabled', satisfied: !!submitToSapEnabled, description: 'Offered only while the shipment is not finished and is ready to be submitted to SAP' },
      notImportedState,
    ],
  });
  const indexFlag = useSecuredFlag('more-action-index', {
    kind: 'action',
    description: 'Reindex expedition',
  });
  const sendWebhookFlag = useSecuredFlag('more-action-send-webhook', {
    kind: 'action',
    description: 'Queue the owner company webhooks for this expedition with current data (MANUAL_ACTION)',
    permissions: [PermissionTypesEnum.PERMISSION_EXPEDITION_DETAIL_TAB_SHIPMENT_RW],
    custom: [{ label: 'company has a webhook definition', satisfied: webhookAvailable === true }],
  });
  const favoriteFlag = useSecuredFlag('more-action-favorite', {
    kind: 'action',
    description: 'Toggle favorite',
    state: [notImportedState],
  });
  const generateConfirmationPdfFlag = useSecuredFlag('more-action-generate-confirmation-pdf', {
    kind: 'action',
    description: 'Generate order-confirmation PDF',
    features: [FeaturesEnum.PDF_ORDER_CONFIRMATION],
    state: [notImportedState],
  });
  const generateArchivePdfFlag = useSecuredFlag('more-action-generate-archive-pdf', {
    kind: 'action',
    description: 'Generate expedition-detail PDF',
    features: [FeaturesEnum.PDF_EXPEDITION_DETAIL],
  });
  const dachserFlag = useSecuredFlag('more-action-dachser', {
    kind: 'action',
    description: 'Dachser API actions (download POD / refresh status)',
    state: [
      { label: 'Dachser API carrier', satisfied: isDachserApiCarrier, description: 'Offered only when the assigned carrier is connected through the Dachser API', byDesign: true },
      notImportedState,
    ],
  });

  interface MenuItemConfig {
    id: string;
    label: string;
    icon: React.ReactNode;
    command: () => void;
    visible: RuleInput;
    section: MenuItemSection;
  }

  const model: MenuItemConfig[] = [
    {
      id: 'duplicate',
      label: tr('AllExpedition.duplicateConfigurable', 'Duplikovat...'),
      icon: <SvgDuplicate />,
      command: () => setVisibleDuplicateDialog(true),
      visible: duplicateFlag,
      section: 'actions',
    },
    {
      id: 'multi-duplicate',
      label: tr('AllExpedition.mutliDuplicateConfigurable', 'Duplikovat více...'),
      icon: <SvgDuplicate />,
      command: () => setVisibleDuplicateSidebar(true),
      visible: duplicateFlag,
      section: 'actions',
    },
    {
      id: 'cancel',
      label: tr('AllExpedition.cancel', 'Cancel'),
      icon: <SvgXcircle />,
      command: () => {
        setCancelationRequest({
          applicationId,
          customerId,
        });
      },
      visible: cancelFlag,
      section: 'state',
    },
    {
      id: 'extra-costs',
      label: tr(`ButtonMoreNavigation.extraCosts`, `Vícenáklady`),
      icon: <SvgCoins />,
      command: () => setVisibleAdditionalCosts(true),
      visible: extraCostsFlag,
      section: 'related',
    },
    {
      id: 'assign-carrier',
      label: tr(`ButtonMoreNavigation.assignManually`, `Priradit dopravce`),
      icon: <SvgCarPlate />,
      command: () => setVisibleAssignCarrierSidebar(true),
      visible: assignCarrierFlag,
      section: 'state',
    },
    {
      id: 'claim',
      label: tr(`ButtonMoreNavigation.claim`, `Claim`),
      icon: <SvgAlert fill={'var(--ringil3-content-secondary)'} />,
      command: () => window.open(`mailto:${assignedDispatcherEmail ? assignedDispatcherEmail : ''}?subject=REKLAMACE%20PŘEPRAVY%20${applicationId}&body=${claim_email_body}`),
      visible: claimFlag,
      section: 'related',
    },
    {
      id: 'notify-with-price',
      label: tr(`ButtonMoreNavigation.notifyWithPrice`, `Notify with price`),
      icon: <SvgMessage />,
      command: () => handleMailto(expeditionId, true),
      visible: notifyFlag,
      section: 'related',
    },
    {
      id: 'notify-without-price',
      label: tr(`ButtonMoreNavigation.notifyWithoutPrice`, `Notify without price`),
      icon: <SvgMessage />,
      command: () => handleMailto(expeditionId, false),
      visible: notifyFlag,
      section: 'related',
    },
    {
      id: 'approve-mailto',
      label: tr(`ButtonMoreNavigation.approveMailto`, `Approve extra transport`),
      icon: <SvgMessage />,
      command: () => handleApproveMailto(expeditionId),
      visible: approveMailtoFlag,
      section: 'related',
    },
    {
      id: 'notify-warehouse',
      label: tr(`ButtonMoreNavigation.notifyWarehouse`, `Notify warehouse`),
      icon: <SvgMessage />,
      command: () => {
        openMailClientCustomsForExp();
      },
      visible: notifyWarehouseFlag,
      section: 'related',
    },
    {
      id: 'assign-myself',
      label: tr(`ButtonMoreNavigation.assignMyself`, `Assign myself`),
      icon: <SvgPerson />,
      command: () => {
        editInternalInfoExpedition(expeditionId, { responsiblePerson: { responsiblePersonId: loggedUser.id } }, () => onReload());
      },
      visible: assignMyselfFlag,
      section: 'related',
    },
    {
      id: 'archive',
      label: tr(`ButtonMoreNavigation.archive`, `Archive`),
      icon: <SvgArchive />,
      command: () => setVisibleConfirmDialog(true),
      visible: archiveFlag,
      section: 'actions',
    },
    {
      id: 'archive-with-sn',
      label: tr(`ButtonMoreNavigation.archiveWithSN`, `Archive with SN`),
      icon: <SvgArchive />,
      command: () => setVisibleConfirmArchiveWithSNDialog(true),
      visible: archiveWithSNFlag,
      section: 'actions',
    },
    {
      id: 'unarchive',
      label: tr(`ButtonMoreNavigation.unarchive`, `Unarchive`),
      icon: <SvgArchive />,
      command: () => setVisibleConfirmDialog(true),
      visible: unarchiveFlag,
      section: 'actions',
    },
    {
      id: 'finish',
      label: tr(`ButtonMoreNavigation.finish`, `Finish`),
      icon: <SvgCheckSquare />,
      command: () => setVisibleFinishDialog(true),
      visible: finishFlag,
      section: 'state',
    },
    {
      id: 'submit-to-sap',
      label: tr(`ButtonMoreNavigation.submitToSap`, `Submit to SAP`),
      icon: <SvgCheckSquare />,
      command: () => setVisibleSapSubmitDialog(true),
      visible: submitToSapFlag,
      section: 'state',
    },
    {
      id: 'index',
      label: tr(`ButtonMoreNavigation.index`, `Index`),
      icon: <SvgNumber />,
      command: () =>
        reindexOneExpedition(expeditionId, () => {
          onReload();
        }),
      visible: indexFlag,
      section: 'actions',
    },
    {
      id: 'send-webhook',
      label: tr(`ButtonMoreNavigation.sendWebhook`, `Send webhook`),
      icon: <SvgMessage />,
      command: () => setVisibleSendWebhookDialog(true),
      visible: sendWebhookFlag,
      section: 'actions',
    },
    {
      id: 'favorite',
      label: isFavorite ? tr(`ButtonMoreNavigation.unfavorite`, `Remove from favorites`) : tr(`ButtonMoreNavigation.favorite`, `Add to favorites`),
      icon: <SvgCoStar fill={isFavorite ? 'var(--ringil3-brand-color)' : 'var(--ringil3-content-secondary)'} />,
      command: () => toggleFavorite(),
      visible: favoriteFlag,
      section: 'actions',
    },
    {
      id: 'generate-confirmation-pdf',
      label: tr(`ButtonMoreNavigation.generateConfirmationPDF`, `Generate confirmation PDF`),
      icon: <SvgTabAttachments />,
      command: () =>
        createOrderConfirmationPdf(expeditionId, () => {
          refreshAttachment();
        }),
      visible: generateConfirmationPdfFlag,
      section: 'actions',
    },
    {
      id: 'generate-archive-pdf',
      label: tr(`ButtonMoreNavigation.generateArchivePDF`, `Generate archive PDF`),
      icon: <SvgTabAttachments />,
      command: () =>
        createExpeditionDetailPdf(expeditionId, () => {
          refreshAttachment();
        }),
      visible: generateArchivePdfFlag,
      section: 'actions',
    },
    {
      id: 'download-dachser-pod',
      label: tr(`ButtonMoreNavigation.downloadDachserPod`, `Download Dachser POD`),
      icon: <SvgTabAttachments />,
      command: () =>
        downloadPODFromDachser(expeditionId, () => {
          refreshAttachment();
        }),
      visible: dachserFlag,
      section: 'actions',
    },
    {
      id: 'refresh-dachser-status',
      label: tr(`ButtonMoreNavigation.refreshDachserStatus`, `Refresh Dachser status`),
      icon: <SvgRepeat />,
      command: () =>
        getDachserStatus(expeditionId, () => {
          onReload();
        }),
      visible: dachserFlag,
      section: 'state',
    },
  ];

  // A section title is only meaningful when the section renders at least one visible item.
  // In security-decoration mode every item is rendered (as a "hidden" box), so keep titles then.

  const sendWebhook = () => {
    setLoading(true);
    manualWebhookExpeditionSend(
      expeditionId,
      () => {
        setLoading(false);
        setVisibleSendWebhookDialog(false);
        // Re-resolves webhookAvailable: the item stays hidden until the queued delivery is sent.
        onReload();
      },
      null,
      {
        onError: (_error, showStandardError) => {
          showStandardError();
          setLoading(false);
        },
      },
    );
  };

  const archive = () => {
    setLoading(true);
    changeArchiveStateForExpedition(
      customerId,
      applicationId,
      {
        archive: !isArchived,
        archiveSN: false,
      },
      () => {
        setLoading(false);
        setVisibleConfirmDialog(false);
        onReload();
      },
      null,
      {
        onError: (_error, showStandardError) => {
          showStandardError();
          setLoading(false);
        },
      },
    );
  };

  const archiveWithSN = () => {
    setLoading(true);
    changeArchiveStateForExpedition(
      customerId,
      applicationId,
      {
        archive: !isArchived,
        archiveSN: true,
      },
      () => {
        setLoading(false);
        setVisibleConfirmArchiveWithSNDialog(false);
        onReload();
      },
      null,
      {
        onError: (_error, showStandardError) => {
          showStandardError();
          setLoading(false);
        },
      },
    );
  };

  const finish = () => {
    setLoading(true);
    finishExpedition(customerId, applicationId, () => {
      setLoading(false);
      setVisibleFinishDialog(false);
      onReload();
    }, null, {
      onError: (_error, showStandardError) => {
        showStandardError();
        setLoading(false);
      },
    });
  };

  const toggleFavorite = () => {
    favoriteExpedition(expeditionId, { favorite: !isFavorite }, () => {
      setIsFavorite(!isFavorite);
      onReload();
    });
  };

  const sendExpeditionToSap = () => {
    setLoading(true);
    sendExternalQuote(
      expeditionId,
      () => {
        setLoading(false);
        onReload();
      },
      null,
      {
        onError: (_error, showStandardError) => {
          showStandardError();
          setLoading(false);
        },
      },
    );
  };

  return (
    <>{decorate(<>
      <MoreButton
        buttonSize={buttonSize}
        dataTestId={dataTestId}
        sections={SECTIONS.map(section => ({
          title: section.title(tr),
          items: model
            .filter(item => item.section === section.id)
            .map(item => ({
              id: item.id,
              label: item.label,
              icon: item.icon,
              command: item.command,
              secured: item.visible,
              dataTestId: `${dataTestId}-menuItem-${item.label.replace(/\s/g, '').replace('...', '').toLowerCase()}`,
            })),
        }))}
      />
      {isNotNullOrUndefined(cancelationRequest) && (
        <CancelExpeditionSidebar
          onContinue={() => {
            onReload();
          }}
        />
      )}

      {visibleAdditionalCosts && (
        <AdditionalCostsSidebar
          visible={visibleAdditionalCosts}
          onHide={() => setVisibleAdditionalCosts(false)}
          onComplete={() => {
            setVisibleAdditionalCosts(false);
            onReload();
          }}
          customerId={customerId}
          currency={currency}
          applicationId={applicationId}
        />
      )}

      <AssignCarrierSidebar
        applicationId={applicationId}
        customerId={customerId}
        expeditionId={expeditionId}
        onContinue={() => {
          onReload();
        }}
      />
      <ConfirmDialog
        visible={visibleConfirmDialog}
        dialogTitle={
          isArchived
            ? tr(`ButtonMoreNavigation.unarchiveTitle`, `Do you want to unarchive this record?`)
            : tr(`ButtonMoreNavigation.archiveTitle`, `Do you want to archive this record?`)
        }
        entity={{
          label: tr(`ButtonMoreNavigation.forExpedition`, `For expedition`),
          value: applicationId,
          iconPath: '/images/icons/ringil3/forExpedition.svg',
        }}
        dialogMessage={
          isArchived ? tr(`ButtonMoreNavigation.body1`, `Do you want to unarchive this record?`) : tr(`ButtonMoreNavigation.body2`, `Do you want to archive this record?`)
        }
        onCancel={() => setVisibleConfirmDialog(false)}
        onConfirm={() => archive()}
        onAcceptButtonText={isArchived ? tr(`ButtonMoreNavigation.yesUnarchive`, `Unarchive`) : tr(`ButtonMoreNavigation.yesArchive`, `Archive`)}
        onCancelButtonText={tr(`ButtonMoreNavigation.close`, `Close`)}
        loading={loading}
      />

      <ConfirmDialog
        visible={visibleSendWebhookDialog}
        dialogTitle={tr(`ButtonMoreNavigation.sendWebhookTitle`, `Send webhook now?`)}
        entity={{
          label: tr(`ButtonMoreNavigation.forExpedition`, `For expedition`),
          value: applicationId,
          iconPath: '/images/icons/ringil3/forExpedition.svg',
        }}
        dialogMessage={tr(
          `ButtonMoreNavigation.sendWebhookBody`,
          `All webhooks configured for your company will be sent again with the current expedition data.`,
        )}
        onCancel={() => setVisibleSendWebhookDialog(false)}
        onConfirm={() => sendWebhook()}
        onAcceptButtonText={tr(`ButtonMoreNavigation.yesSendWebhook`, `Send`)}
        onCancelButtonText={tr(`ButtonMoreNavigation.close`, `Close`)}
        loading={loading}
      />

      <ConfirmDialog
        visible={visibleConfirmArchiveWithSNDialog}
        dialogTitle={
          isArchived
            ? tr(`ButtonMoreNavigation.unarchiveWithSNTitle`, `Do you want to unarchive this record?`)
            : tr(`ButtonMoreNavigation.archiveWithSNTitle`, `Do you want to archive this record?`)
        }
        entity={{
          label: tr(`ButtonMoreNavigation.forExpedition`, `For expedition`),
          value: applicationId,
          iconPath: '/images/icons/ringil3/forExpedition.svg',
        }}
        dialogMessage={
          isArchived
            ? tr(`ButtonMoreNavigation.body1WithSN`, `Do you want to unarchive this record? `)
            : tr(`ButtonMoreNavigation.body2WithSN`, `Do you want to archive this record? Linked SN will be archived too.`)
        }
        onCancel={() => setVisibleConfirmArchiveWithSNDialog(false)}
        onConfirm={() => archiveWithSN()}
        onAcceptButtonText={isArchived ? tr(`ButtonMoreNavigation.yesUnarchive`, `Unarchive`) : tr(`ButtonMoreNavigation.yesArchive`, `Archive`)}
        onCancelButtonText={tr(`ButtonMoreNavigation.close`, `Close`)}
        loading={loading}
      />

      <ConfirmDialog
        visible={visibleFinishDialog}
        dialogTitle={tr('ButtonMoreNavigation.doYouWantToFinishTheExpedition', 'Do you want to finish the expedition?')}
        dialogMessage={tr('ButtonMoreNavigation.doYouWantToFinishTheExpedition', 'Do you want to finish the expedition?')}
        onCancel={() => setVisibleFinishDialog(false)}
        onConfirm={() => finish()}
        onAcceptButtonText={tr(`ButtonMoreNavigation.finish`, `Finish`)}
        onCancelButtonText={tr(`ButtonMoreNavigation.close`, `Close`)}
        loading={loading}
      />

      <ConfirmDialog
        visible={visibleSapSubmitDialog}
        dialogTitle={tr('ButtonMoreNavigation.doYouWantToSubmitToSapTheExpeditionTitle', 'Do you want to submit to SAP the expedition?')}
        dialogMessage={tr('ButtonMoreNavigation.doYouWantToSubmitToSapTheExpeditionMessage', 'Do you want to submit to SAP the expedition?')}
        onCancel={() => setVisibleSapSubmitDialog(false)}
        onConfirm={() => {
          sendExpeditionToSap();
          setVisibleSapSubmitDialog(false);
        }}
        onAcceptButtonText={tr(`ButtonMoreNavigation.submit`, `Submit`)}
        onCancelButtonText={tr(`ButtonMoreNavigation.close`, `Close`)}
        loading={loading}
      />
      <DuplicateExpeditionDialog
        applicationId={applicationId}
        expeditionId={expeditionId}
        visible={visibleDuplicateDialog}
        onCancel={() => setVisibleDuplicateDialog(false)}
        isLinkedToSN={linkedToSN}
      />
      {visibleDuplicateSidebar && (
        <DuplicateExpeditionSidebar
          expeditionId={expeditionId}
          onSuccess={() => {
            setVisibleDuplicateSidebar(false);
            onReload();
          }}
          expeditionDuplicationOptions={expeditionDuplicationOptions}
        />
      )}
    </>)}</>
  );
};







export default ExpeditionMoreButtons;
